// Tank Register — RBM's tank farm sheet in the CRM: a column per tank
// (T1…T17, coloured as on the sheet), a row per date, each cell typed the
// way the team already writes it ("SN70 F", "E", "350 4K", "SN 70 R").
// Server: src/api-inventory/tanks.js.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Droplets, Gauge, Save, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { cn, localToday } from '@/shared/utils/utils';
import { useUser } from '@/shared/hooks/useAuth';
import {
  useTankRegister, useSaveTankDay, useSaveTank, importTankSheet, materialErrorText, downloadCsv,
  type Tank, type TankEntry, type TankStatus,
} from '../hooks/useMaterials';
import { useTankSummary } from '../hooks/useStockControl';
import { Panel, fmtDateTime, fmtDay } from './materialShared';

// Header bands, matching the sheet's column colours.
export const GROUP_BAND: Record<Tank['group'], string> = {
  yellow: 'bg-[#f6d860] text-[#3d3200]',
  green: 'bg-[#3ed16a] text-[#06341a]',
  white: 'bg-surface-container-low text-on-surface',
  amber: 'bg-[#f0c040] text-[#3d2e00]',
  orange: 'bg-[#f59b2b] text-[#3d1f00]',
  red: 'bg-[#ef4444] text-white',
};
const STATUS_CELL: Record<TankStatus, string> = {
  full: 'bg-success/15 text-success-text',
  running: 'bg-info/15 text-info-text',
  partial: 'bg-warning/15 text-warning-text',
  empty: 'bg-surface-container-low text-on-surface-subtle',
  leakage: 'bg-danger/15 text-danger-text',
  other: 'text-on-surface',
};
const LEGEND: [TankStatus, string, string][] = [
  ['full', 'F', 'Full'], ['running', 'R', 'Running (in use)'], ['partial', '4K', 'Partly filled (KL)'], ['empty', 'E', 'Empty'], ['leakage', 'Leakage', 'Out of use'],
];
const isApprover = (role?: string) => ['superadmin', 'owner', 'admin'].includes(String(role || ''));
const daysAgo = (n: number) => localToday(new Date(Date.now() - n * 864e5));

export function TankRegisterTab() {
  const user = useUser();
  const admin = isApprover(user?.role);
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(localToday());
  const [day, setDay] = useState(localToday());
  const [importing, setImporting] = useState(false);
  const [capacities, setCapacities] = useState(false);
  const { data, isLoading } = useTankRegister(from, to);
  const { data: summary } = useTankSummary();
  // The editor row reads the chosen day even when it is outside the list range.
  const { data: dayData } = useTankRegister(day, day);
  const save = useSaveTankDay();

  const tanks = useMemo(() => (data?.tanks || []).filter((t) => t.isActive), [data?.tanks]);
  const byDay = useMemo(() => {
    const m = new Map<string, Map<string, TankEntry>>();
    for (const e of data?.entries || []) {
      if (!m.has(e.dateKey)) m.set(e.dateKey, new Map());
      m.get(e.dateKey)!.set(e.tankId, e);
    }
    return m;
  }, [data?.entries]);
  const days = useMemo(() => [...byDay.keys()].sort().reverse(), [byDay]);
  const latest = useMemo(() => new Map((dayData?.latest || data?.latest || []).map((e) => [e.tankId, e])), [dayData?.latest, data?.latest]);
  const savedForDay = useMemo(() => new Map((dayData?.entries || []).filter((e) => e.dateKey === day).map((e) => [e.tankId, e])), [dayData?.entries, day]);

  // Editor: the day's saved text, else the latest reading carried down (as the
  // team copies yesterday's row in Excel and changes only what changed).
  const [draft, setDraft] = useState<Record<string, string>>({});
  useEffect(() => {
    const next: Record<string, string> = {};
    for (const t of tanks) next[t._id] = savedForDay.get(t._id)?.raw ?? latest.get(t._id)?.raw ?? '';
    setDraft(next);
  }, [tanks, savedForDay, latest]);
  const hasSavedDay = savedForDay.size > 0;
  const changed = tanks.filter((t) => (draft[t._id] ?? '') !== (savedForDay.get(t._id)?.raw ?? ''));

  const saveDay = async () => {
    try {
      const cells = (hasSavedDay ? changed : tanks).map((t) => ({ tankId: t._id, text: draft[t._id] ?? '' }));
      const r = await save.mutateAsync({ dateKey: day, cells });
      toast.success(`Saved ${fmtDay(day)}: ${r.created} new, ${r.updated} changed${r.cleared ? `, ${r.cleared} cleared` : ''}`);
    } catch (err) {
      toast.error(materialErrorText(err, 'Could not save the readings.'));
    }
  };

  const counts = useMemo(() => {
    const c: Record<TankStatus, number> = { full: 0, running: 0, partial: 0, empty: 0, leakage: 0, other: 0 };
    let kl = 0;
    for (const t of tanks) {
      const e = latest.get(t._id);
      if (!e) continue;
      c[e.status] += 1;
      if (e.status === 'partial' && e.quantityKl) kl += e.quantityKl;
    }
    return { ...c, kl };
  }, [tanks, latest]);

  const exportCsv = () => downloadCsv(`tank-register-${from}-to-${to}.csv`, ['Date', ...tanks.map((t) => t.code)],
    days.map((d) => [fmtDay(d), ...tanks.map((t) => byDay.get(d)?.get(t._id)?.raw ?? '')]));

  if (isLoading) return <Skeleton className="h-[420px]" />;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {([['full', 'Full'], ['running', 'Running'], ['partial', 'Partly filled'], ['empty', 'Empty'], ['leakage', 'Leakage']] as [TankStatus, string][]).map(([k, label]) => (
          <div key={k} className="rounded-card border border-border bg-card px-4 py-3 shadow-surface-1">
            <div className="text-body-sm text-on-surface-variant">{label}</div>
            <div className="mt-1 font-mono text-[22px] font-semibold leading-tight tabular-nums text-on-surface">{counts[k]}</div>
            <div className="text-data-xs text-on-surface-subtle">{k === 'partial' && counts.kl ? `${counts.kl} KL in all` : `tank${counts[k] === 1 ? '' : 's'}, latest reading`}</div>
          </div>
        ))}
      </div>

      <Panel
        title="Bulk stock by grade"
        subtitle="From each tank's latest reading: Full counts as the tank's capacity, 4K as 4 KL, Empty as 0."
        actions={admin ? <Button variant="outline" onClick={() => setCapacities(true)}><Gauge className="h-4 w-4" /> Tank capacities</Button> : undefined}
      >
        {!!summary?.capacityMissing && (
          <p className="mx-5 mt-3 rounded-lg border border-info/25 bg-info/10 px-3 py-2 text-body-sm text-info-text">
            {summary.capacityMissing} tank{summary.capacityMissing === 1 ? ' has' : 's have'} no capacity entered, so a Full reading cannot be counted in KL yet.{admin ? ' Enter them under Tank capacities.' : ''}
          </p>
        )}
        <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-3 lg:grid-cols-6">
          {(summary?.grades || []).map((g) => (
            <div key={g.product} className="rounded-card-sm border border-border bg-card px-3 py-2.5">
              <div className="text-body-sm font-semibold text-on-surface">{g.product}</div>
              <div className="font-mono text-[20px] font-semibold tabular-nums text-on-surface">{g.kl.toLocaleString('en-IN')} <span className="text-body-sm text-on-surface-subtle">KL</span></div>
              <div className="text-data-xs text-on-surface-subtle">{g.tanks.join(', ')}{g.unknownTanks ? ` · ${g.unknownTanks} not measured` : ''}</div>
            </div>
          ))}
          {!summary?.grades.length && <div className="col-span-full text-body-sm text-on-surface-variant">No readings yet.</div>}
        </div>
      </Panel>

      <Panel
        title="Tank register"
        subtitle="Type each tank as on the sheet: SN70 F, E, 350 4K, SN 70 R, sn500 Y F, Leakage."
        actions={<>
          <Button variant="outline" onClick={exportCsv} disabled={!days.length}><Download className="h-4 w-4" /> Excel</Button>
          {admin && <Button variant="outline" onClick={() => setImporting(true)}><Upload className="h-4 w-4" /> Import sheet</Button>}
        </>}
      >
        <div className="flex flex-wrap items-end justify-between gap-3 px-5 py-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-body-sm">
            {LEGEND.map(([s, code, label]) => (
              <span key={s} className="inline-flex items-center gap-1.5">
                <span className={cn('rounded px-1.5 py-0.5 font-mono text-data-xs font-semibold', STATUS_CELL[s])}>{code}</span>
                <span className="text-on-surface-variant">{label}</span>
              </span>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-40"><Input type="date" label="Show from" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></div>
            <div className="w-40"><Input type="date" label="To" value={to} min={from} max={localToday()} onChange={(e) => setTo(e.target.value)} /></div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1640px] border-separate border-spacing-0 text-body-sm">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 w-32 border-b border-border bg-card px-3 py-2 text-left text-label-caps uppercase text-on-surface-variant">Date</th>
                {tanks.map((t) => (
                  <th key={t._id} className={cn('border-b border-l border-border/60 px-1 py-2 text-center font-semibold', GROUP_BAND[t.group])} title={`${t.name}${t.groupLabel ? ` · usually ${t.groupLabel}` : ''}${t.capacityKl ? ` · ${t.capacityKl} KL` : ''}`}>
                    {t.code}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="bg-primary-container/40">
                <td className="sticky left-0 z-10 border-b border-border bg-primary-container/60 px-2 py-2 align-top">
                  <input type="date" value={day} max={localToday()} onChange={(e) => setDay(e.target.value)} aria-label="Reading date" className="h-8 w-full rounded-md border border-input bg-surface px-1.5 text-data-xs outline-none focus:border-primary" />
                  <Button size="sm" className="mt-1.5 w-full" onClick={saveDay} loading={save.isPending} disabled={hasSavedDay && !changed.length}>
                    <Save className="h-3.5 w-3.5" /> {hasSavedDay ? `Save ${changed.length || ''}`.trim() : 'Save day'}
                  </Button>
                </td>
                {tanks.map((t) => {
                  const dirty = (draft[t._id] ?? '') !== (savedForDay.get(t._id)?.raw ?? '');
                  return (
                    <td key={t._id} className="border-b border-l border-border/60 p-1 align-top">
                      <input
                        value={draft[t._id] ?? ''}
                        onChange={(e) => setDraft({ ...draft, [t._id]: e.target.value })}
                        aria-label={`${t.code} on ${day}`}
                        className={cn('h-8 w-full min-w-[84px] rounded-md border bg-surface px-1.5 text-center font-mono text-data-xs uppercase outline-none focus:border-primary', dirty ? 'border-warning ring-1 ring-warning/40' : 'border-input')}
                      />
                    </td>
                  );
                })}
              </tr>
              {days.map((d) => (
                <tr key={d} className={cn('cursor-pointer hover:bg-surface-container-low/60', d === day && 'outline outline-1 outline-primary/40')} onClick={() => setDay(d)}>
                  <td className="sticky left-0 z-10 border-b border-border bg-card px-3 py-1.5 font-medium text-on-surface">{fmtDay(d)}</td>
                  {tanks.map((t) => {
                    const e = byDay.get(d)?.get(t._id);
                    return (
                      <td
                        key={t._id}
                        className={cn('border-b border-l border-border/60 px-1 py-1.5 text-center font-mono text-data-xs font-semibold', e ? STATUS_CELL[e.status] : 'text-on-surface-subtle')}
                        title={e ? `${e.raw} · ${data?.statuses?.[e.status] || e.status}${e.quantityKl ? ` · ${e.quantityKl} KL` : ''}\n${e.enteredByName} · ${fmtDateTime(e.updatedAt)}${e.history.length ? `\nEarlier: ${e.history.map((h) => h.raw).join(' → ')}` : ''}` : ''}
                      >
                        {e ? (e.status === 'leakage' ? 'LEAKAGE' : e.raw.toUpperCase()) : ''}
                        {!!e?.history.length && <span className="ml-0.5 text-[9px] align-super text-on-surface-subtle">✎</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {!days.length && (
            <div className="flex flex-col items-center gap-2 px-5 py-10 text-center">
              <Droplets className="h-8 w-8 text-on-surface-subtle" aria-hidden />
              <p className="text-body-md font-medium text-on-surface">No readings in this period</p>
              <p className="text-body-sm text-on-surface-variant">Fill today's row above and save{admin ? ', or import the existing Excel sheet' : ''}.</p>
            </div>
          )}
        </div>
      </Panel>
      <ImportSheetModal open={importing} onClose={() => setImporting(false)} />
      {capacities && <CapacitiesModal tanks={data?.tanks || []} onClose={() => setCapacities(false)} />}
    </div>
  );
}

function ImportSheetModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof importTankSheet>> | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setFile(null); setPreview(null); } }, [open]);

  const check = async (f: File) => {
    setFile(f); setBusy(true);
    try { setPreview(await importTankSheet(f, true)); } catch (err) { toast.error(materialErrorText(err, 'Could not read the file.')); setPreview(null); } finally { setBusy(false); }
  };
  const run = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await importTankSheet(file, false);
      toast.success(`Imported ${r.days} days: ${r.created ?? 0} new, ${r.updated ?? 0} changed`);
      onClose();
    } catch (err) {
      toast.error(materialErrorText(err, 'Import failed.'));
    } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} size="md" title="Import the tank sheet" footer={(
      <><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={run} loading={busy} disabled={!preview || !preview.cells}>Import {preview ? `${preview.cells} readings` : ''}</Button></>
    )}>
      <div className="space-y-3 text-body-sm">
        <p className="text-on-surface-variant">Upload the Excel exactly as kept today: a header row with T1, T2 … T17, then one row per date (05/08/26 or 2/9/2026) with the date in the first column. Existing readings for the same date and tank are updated; their earlier text is kept.</p>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) check(f); }} />
        <Button variant="outline" onClick={() => fileRef.current?.click()} loading={busy && !preview}><Upload className="h-4 w-4" /> {file ? file.name : 'Choose file'}</Button>
        {preview && (
          <div className="rounded-lg border border-border bg-surface-container-low/60 p-3">
            <div className="font-medium text-on-surface">{preview.days} days · {preview.cells} readings</div>
            <div className="text-on-surface-variant">{fmtDay(preview.from)} to {fmtDay(preview.to)}{preview.skippedRows ? ` · ${preview.skippedRows} rows without a date skipped` : ''}</div>
            {!!preview.unknownTanks.length && <div className="mt-1 text-warning-text">Not set up as tanks, so skipped: {preview.unknownTanks.join(', ')}</div>}
          </div>
        )}
      </div>
    </Modal>
  );
}

export function CapacitiesModal({ tanks, onClose }: { tanks: Tank[]; onClose: () => void }) {
  const save = useSaveTank();
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(tanks.map((t) => [t._id, t.capacityKl ? String(t.capacityKl) : ''])));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const changed = tanks.filter((t) => (values[t._id] || '') !== (t.capacityKl ? String(t.capacityKl) : ''));
      for (const t of changed) await save.mutateAsync({ id: t._id, capacityKl: Number(values[t._id]) || null });
      toast.success(`Saved ${changed.length} tank capacit${changed.length === 1 ? 'y' : 'ies'}`);
      onClose();
    } catch (err) { toast.error(materialErrorText(err, 'Could not save.')); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} size="lg" title="Tank capacities (KL)" footer={(
      <><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} loading={busy}>Save</Button></>
    )}>
      <p className="mb-3 text-body-sm text-on-surface-variant">The full working capacity of each tank in kilolitres (1 KL = 1,000 litres).</p>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
        {tanks.map((t) => (
          <label key={t._id} className="block">
            <span className={cn('mb-1 block rounded px-1.5 py-0.5 text-center text-data-xs font-semibold', GROUP_BAND[t.group])}>{t.code}</span>
            <input type="number" min="0" step="any" value={values[t._id] ?? ''} onChange={(e) => setValues({ ...values, [t._id]: e.target.value })} className="h-9 w-full rounded-lg border border-input bg-surface px-2 text-right font-mono text-body-sm outline-none focus:border-primary" placeholder="KL" />
          </label>
        ))}
      </div>
    </Modal>
  );
}

export default TankRegisterTab;
